"use client";

import { History, LogIn, MessageSquare, RefreshCw, Send, ShieldAlert } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import { API_URL, apiFetch } from "../lib/api";
import {
  claimSimulatorChatIntent,
  clearSimulatorChatIntent,
  dedupeSimulatorChatMessages,
  emitSimulatorChatIntent,
  hasSimulatorChatDraftChanged,
  inspectSimulatorChatIntent,
  isSimulatorChatIntentBlocking,
  loadSimulatorChatIntent,
  mergeSimulatorChatMessage,
  normalizeSimulatorChatHistory,
  normalizeSimulatorChatMessage,
  postSimulatorChatIntent,
  runSimulatorChatSingleFlight,
  safeSimulatorChatTime,
  saveSimulatorChatIntent,
  shouldKeepSimulatorChatIntent,
  updateSimulatorChatIntent,
} from "../lib/simulatorChatIntent";

const DEFAULT_ADAPTERS = Object.freeze({});

export function SimulatorChatClient({ simulatorSlug, adapters = DEFAULT_ADAPTERS }) {
  const request = adapters.request || apiFetch;
  const socketFactory = adapters.socketFactory || io;
  const apiUrl = adapters.apiUrl || API_URL;
  const outboundTransport = adapters.outboundTransport === "rest" ? "rest" : "socket";
  const [state, setState] = useState(initialState);
  const [message, setMessage] = useState("");
  const socketRef = useRef(null);
  const actorRef = useRef(null);
  const intentRef = useRef(null);
  const messageRef = useRef(message);
  const listRef = useRef(null);
  const generationRef = useRef(0);
  const mountedRef = useRef(false);
  const singleFlightRef = useRef(null);

  messageRef.current = message;

  const canSend = state.authState === "authenticated"
    && state.joined
    && !state.busy
    && !state.storageError
    && !isIntentBlockingForUi(state.intent)
    && message.trim().length > 0;

  useEffect(() => {
    mountedRef.current = true;
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    const timers = new Set();
    actorRef.current = null;
    intentRef.current = null;
    singleFlightRef.current = null;
    setState(initialState());

    const isCurrent = () => mountedRef.current && generationRef.current === generation;
    const intentOptions = () => adapterIntentOptions(adapters, isCurrent);

    async function boot() {
      let me;
      try {
        me = await request("/api/v1/auth/me", { cache: "no-store", timeoutMs: 8000 });
      } catch (error) {
        if (!isCurrent()) return;
        if (error?.status === 401) {
          setState((current) => ({ ...current, authState: "anonymous", loading: false }));
        } else if (error?.status === 403) {
          setState((current) => ({ ...current, authState: "denied", loading: false, error: "Tu sesión no tiene acceso a este chat." }));
        } else {
          setState((current) => ({ ...current, authState: "unavailable", loading: false, error: "No pudimos comprobar tu sesión. Intenta actualizar la página." }));
        }
        return;
      }

      const actorId = positiveId(me?.user?.id);
      if (!actorId) {
        if (!isCurrent()) return;
        setState((current) => ({ ...current, authState: "unavailable", loading: false, error: "Nitro devolvió una sesión que no se pudo verificar." }));
        return;
      }
      actorRef.current = actorId;

      let restoredIntent = null;
      let storageError = "";
      try {
        restoredIntent = await loadSimulatorChatIntent(actorId, simulatorSlug, intentOptions());
      } catch (error) {
        storageError = error?.message || "El navegador no pudo comprobar mensajes pendientes.";
      }
      if (!isCurrent()) return;
      intentRef.current = restoredIntent;
      setState((current) => ({
        ...current,
        authState: "authenticated",
        loading: false,
        user: me.user,
        intent: restoredIntent,
        storageError,
        notice: restoredIntent ? restoredIntentNotice(restoredIntent) : "",
      }));
      connectSocket();
    }

    function connectSocket() {
      if (!isCurrent()) return;
      let socket;
      try {
        socket = socketFactory(apiUrl, { withCredentials: true, transports: ["websocket", "polling"] });
      } catch {
        setState((current) => ({ ...current, error: "No se pudo conectar al chat.", joined: false }));
        return;
      }
      socketRef.current = socket;

      socket.on("connect", () => {
        if (!isCurrent()) return;
        setState((current) => ({ ...current, socketConnected: true, joined: false, error: "" }));
        let settled = false;
        const timer = setTimeout(() => {
          timers.delete(timer);
          if (settled || !isCurrent()) return;
          settled = true;
          setState((current) => ({ ...current, joined: false, error: "No se pudo confirmar el acceso al chat a tiempo." }));
        }, adapters.joinTimeoutMs || 12000);
        timers.add(timer);
        socket.emit("simulator:join", { simulatorSlug, limit: 50 }, (response) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          timers.delete(timer);
          if (!isCurrent()) return;
          if (!response?.ok) {
            setState((current) => ({ ...current, joined: false, error: safePublicError(response?.error, "No se pudo abrir el chat.") }));
            return;
          }
          try {
            const history = normalizeSimulatorChatHistory(response, simulatorSlug);
            setState((current) => ({
              ...current,
              joined: true,
              messages: dedupeSimulatorChatMessages([...current.messages, ...history.messages]),
              error: "",
            }));
          } catch (error) {
            setState((current) => ({ ...current, joined: false, error: error.message }));
          }
        });
      });

      socket.on("disconnect", () => {
        if (!isCurrent()) return;
        setState((current) => ({ ...current, socketConnected: false, joined: false }));
      });
      socket.on("connect_error", () => {
        if (!isCurrent()) return;
        setState((current) => ({ ...current, socketConnected: false, joined: false, error: "No se pudo conectar al chat." }));
      });
      socket.on("simulator:message", (payload) => {
        if (!isCurrent() || payload?.simulator?.slug !== simulatorSlug) return;
        try {
          const incoming = normalizeSimulatorChatMessage(payload.message);
          setState((current) => ({ ...current, messages: mergeSimulatorChatMessage(current.messages, incoming) }));
        } catch {
          // Ignore unverifiable broadcasts without breaking the existing chat.
        }
      });
    }

    boot();
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
      socketRef.current?.disconnect();
      socketRef.current = null;
      actorRef.current = null;
      intentRef.current = null;
      singleFlightRef.current = null;
    };
  }, [adapters, apiUrl, request, simulatorSlug, socketFactory]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [state.messages.length]);

  function sendMessage(event) {
    event.preventDefault();
    if (!canSend || !socketRef.current) return;
    const capturedText = message;
    const actorId = actorRef.current;
    const generation = generationRef.current;
    const isCurrent = () => mountedRef.current && generationRef.current === generation;
    const intentOptions = () => adapterIntentOptions(adapters, isCurrent);

    runSimulatorChatSingleFlight(singleFlightRef, async () => {
      if (!isCurrent() || !actorId) return;
      setState((current) => ({ ...current, busy: true, error: "", notice: "Preparando un envío verificable..." }));
      let intent;
      try {
        const previous = intentRef.current;
        if (previous?.status === "failed") {
          await clearSimulatorChatIntent(actorId, simulatorSlug, previous.clientRequestId, intentOptions());
          if (!isCurrent()) return;
          intentRef.current = null;
          setState((current) => ({ ...current, intent: null }));
        }
        const claim = await claimSimulatorChatIntent({ actorId, simulatorSlug, message: capturedText }, intentOptions());
        if (!isCurrent()) return;
        intent = claim.intent;
        intentRef.current = intent;
        if (!claim.created) {
          setState((current) => ({ ...current, busy: false, intent, notice: restoredIntentNotice(intent) }));
          return;
        }
        intent = updateSimulatorChatIntent(intent, { found: true, status: "processing", messageId: null, errorCode: null }, intentOptions());
        const saved = await saveSimulatorChatIntent(actorId, intent, intentOptions());
        if (!saved || !isCurrent()) return;
        intent = saved;
        intentRef.current = intent;
        setState((current) => ({ ...current, intent, notice: "Enviando el mensaje..." }));

        const outbound = {
          simulatorSlug,
          clientRequestId: intent.clientRequestId,
          message: capturedText,
        };
        const outcome = outboundTransport === "rest"
          ? await postSimulatorChatIntent(outbound, { request, timeoutMs: adapters.restTimeoutMs || 8000 })
          : await emitSimulatorChatIntent(socketRef.current, outbound, { timeoutMs: adapters.ackTimeoutMs || 12000 });
        if (!isCurrent() || intentRef.current?.clientRequestId !== intent.clientRequestId) return;
        await applyOutcome({ actorId, generation, intent, outcome });
      } catch (error) {
        if (!isCurrent()) return;
        if (intent && shouldKeepSimulatorChatIntent(error)) {
          const unknown = updateSimulatorChatIntent(intent, { found: false }, intentOptions());
          const saved = await saveSimulatorChatIntent(actorId, unknown, intentOptions()).catch(() => null);
          if (!isCurrent()) return;
          intentRef.current = saved || unknown;
          setState((current) => ({
            ...current,
            busy: false,
            intent: saved || unknown,
            notice: "No pudimos confirmar el envío. Conservamos la solicitud; consulta su estado sin reenviar.",
          }));
          return;
        }
        if (intent) {
          const failed = updateSimulatorChatIntent(intent, { found: true, status: "failed", messageId: null, errorCode: error?.code || null }, intentOptions());
          const saved = await saveSimulatorChatIntent(actorId, failed, intentOptions()).catch(() => null);
          if (!isCurrent()) return;
          intentRef.current = saved || failed;
          setState((current) => ({ ...current, intent: saved || failed }));
        }
        setState((current) => ({
          ...current,
          busy: false,
          error: error?.status === 403 ? "Tu sesión no tiene acceso para enviar este mensaje." : error?.message || "No se pudo enviar el mensaje.",
          notice: "El mensaje no quedó confirmado.",
        }));
      } finally {
        if (isCurrent()) setState((current) => ({ ...current, busy: false }));
      }
    });
  }

  function inspectPendingIntent() {
    const intent = intentRef.current;
    const actorId = actorRef.current;
    if (!intent || !actorId || state.busy) return;
    const generation = generationRef.current;
    const isCurrent = () => mountedRef.current && generationRef.current === generation;
    const intentOptions = () => adapterIntentOptions(adapters, isCurrent);
    runSimulatorChatSingleFlight(singleFlightRef, async () => {
      setState((current) => ({ ...current, busy: true, error: "", notice: "Consultando la solicitud guardada..." }));
      try {
        const outcome = await inspectSimulatorChatIntent(simulatorSlug, intent.clientRequestId, { request, timeoutMs: 8000 });
        if (!isCurrent() || intentRef.current?.clientRequestId !== intent.clientRequestId) return;
        await applyOutcome({ actorId, generation, intent, outcome });
      } catch (error) {
        if (!isCurrent()) return;
        setState((current) => ({
          ...current,
          notice: "No pudimos confirmar el estado. Conservamos la misma solicitud para otra consulta.",
          error: error?.message || "No se pudo consultar el mensaje.",
        }));
      } finally {
        if (isCurrent()) setState((current) => ({ ...current, busy: false }));
      }
    });
  }

  function loadHistoryForCompletedIntent() {
    const intent = intentRef.current;
    const actorId = actorRef.current;
    if (!intent || intent.status !== "completed" || !intent.messageId || !actorId || state.busy) return;
    const generation = generationRef.current;
    const isCurrent = () => mountedRef.current && generationRef.current === generation;
    const intentOptions = () => adapterIntentOptions(adapters, isCurrent);
    runSimulatorChatSingleFlight(singleFlightRef, async () => {
      setState((current) => ({ ...current, busy: true, error: "", notice: "Cargando el mensaje confirmado..." }));
      try {
        const data = await request(`/api/v1/simulator-chat/${encodeURIComponent(simulatorSlug)}/messages?limit=50`, { cache: "no-store", timeoutMs: 8000 });
        const history = normalizeSimulatorChatHistory(data, simulatorSlug);
        if (!isCurrent() || intentRef.current?.clientRequestId !== intent.clientRequestId) return;
        const recovered = history.messages.find((item) => item.id === intent.messageId);
        if (!recovered) {
          setState((current) => ({
            ...current,
            messages: dedupeSimulatorChatMessages([...current.messages, ...history.messages]),
            notice: "El mensaje está confirmado, pero aún no aparece en el historial. Puedes volver a cargarlo sin reenviar.",
          }));
          return;
        }
        const draftChanged = await hasSimulatorChatDraftChanged(intent, messageRef.current, intentOptions()).catch(() => true);
        if (!isCurrent()) return;
        const cleared = await clearSimulatorChatIntent(actorId, simulatorSlug, intent.clientRequestId, intentOptions()).catch(() => false);
        if (!isCurrent()) return;
        if (!cleared) {
          setState((current) => ({
            ...current,
            messages: dedupeSimulatorChatMessages([...current.messages, ...history.messages]),
            notice: "Mensaje recuperado. El navegador no pudo cerrar el marcador local; no se reenviará.",
          }));
          return;
        }
        intentRef.current = null;
        setState((current) => ({
          ...current,
          messages: dedupeSimulatorChatMessages([...current.messages, ...history.messages]),
          intent: null,
          notice: "Mensaje confirmado y recuperado del historial.",
        }));
        if (!draftChanged) setMessage("");
      } catch (error) {
        if (!isCurrent()) return;
        setState((current) => ({
          ...current,
          notice: "No pudimos cargar el historial. El envío confirmado sigue protegido y no se reenviará.",
          error: error?.message || "No se pudo cargar el historial.",
        }));
      } finally {
        if (isCurrent()) setState((current) => ({ ...current, busy: false }));
      }
    });
  }

  async function applyOutcome({ actorId, generation, intent, outcome }) {
    const isCurrent = () => mountedRef.current && generationRef.current === generation;
    const intentOptions = () => adapterIntentOptions(adapters, isCurrent);
    const next = updateSimulatorChatIntent(intent, outcome, intentOptions());
    const saved = await saveSimulatorChatIntent(actorId, next, intentOptions());
    if (!saved || !isCurrent() || intentRef.current?.clientRequestId !== intent.clientRequestId) return;
    intentRef.current = saved;

    if (outcome?.status === "completed" && outcome.message) {
      const draftChanged = await hasSimulatorChatDraftChanged(saved, messageRef.current, intentOptions()).catch(() => true);
      if (!isCurrent() || intentRef.current?.clientRequestId !== intent.clientRequestId) return;
      const cleared = await clearSimulatorChatIntent(actorId, simulatorSlug, intent.clientRequestId, intentOptions()).catch(() => false);
      if (!isCurrent()) return;
      if (!cleared) {
        setState((current) => ({
          ...current,
          busy: false,
          intent: saved,
          messages: mergeSimulatorChatMessage(current.messages, outcome.message),
          notice: "Mensaje confirmado. Conservamos su identidad porque el navegador no pudo cerrar el marcador local.",
          error: "",
        }));
        return;
      }
      intentRef.current = null;
      setState((current) => ({
        ...current,
        busy: false,
        intent: null,
        messages: mergeSimulatorChatMessage(current.messages, outcome.message),
        notice: "Mensaje confirmado.",
        error: "",
      }));
      if (!draftChanged) setMessage("");
      return;
    }

    setState((current) => ({ ...current, busy: false, intent: saved, notice: outcomeNotice(outcome), error: "" }));
  }

  const statusLabel = useMemo(() => {
    if (state.loading) return "Cargando";
    if (state.authState === "anonymous") return "Solo pilotos";
    if (state.authState === "denied") return "Acceso denegado";
    if (state.authState === "unavailable") return "Sesión no disponible";
    if (state.joined) return "En línea";
    return state.socketConnected ? "Autorizando" : "Reconectando";
  }, [state.authState, state.joined, state.loading, state.socketConnected]);

  const showAuthenticatedChat = state.authState === "authenticated";
  return (
    <article className="simulator-side-card simulator-chat-card" aria-busy={state.busy ? "true" : undefined}>
      <header><strong>Chat Discord</strong><span>{statusLabel}</span></header>

      {state.authState === "anonymous" && !state.loading ? (
        <div className="simulator-chat-empty">
          <MessageSquare aria-hidden="true" size={22} />
          <p>Inicia sesión como piloto para leer y escribir en este chat.</p>
          <a className="nitro-outline-button" href="/login"><LogIn aria-hidden="true" size={16} /> Iniciar sesión</a>
        </div>
      ) : ["denied", "unavailable"].includes(state.authState) ? (
        <div className="simulator-chat-empty"><ShieldAlert aria-hidden="true" size={22} /><p>{state.error}</p></div>
      ) : (
        <div className="simulator-chat-messages" ref={listRef}>
          {state.messages.length ? state.messages.map((item) => (
            <ChatMessage item={item} key={item.id} mine={item.author?.userId === state.user?.id} />
          )) : (
            <div className="simulator-chat-empty simulator-chat-empty--compact">
              <MessageSquare aria-hidden="true" size={22} />
              <p>{state.loading ? "Cargando chat..." : "Aún no hay mensajes en este simulador."}</p>
            </div>
          )}
        </div>
      )}

      {state.storageError ? <p className="simulator-chat-error" role="alert">{state.storageError} No se enviará ningún mensaje.</p> : null}
      {state.error && showAuthenticatedChat ? <p className="simulator-chat-error" role="alert">{state.error}</p> : null}
      {state.notice ? <p className="simulator-chat-notice" aria-live="polite" role="status">{state.notice}</p> : null}

      {state.intent && isSimulatorChatIntentBlocking(state.intent) ? (
        <button className="nitro-outline-button simulator-chat-recovery" disabled={state.busy} onClick={inspectPendingIntent} type="button">
          <RefreshCw aria-hidden="true" size={16} /> Consultar estado
        </button>
      ) : null}
      {state.intent?.status === "completed" && state.intent.messageId ? (
        <button className="nitro-outline-button simulator-chat-recovery" disabled={state.busy} onClick={loadHistoryForCompletedIntent} type="button">
          <History aria-hidden="true" size={16} /> Cargar mensaje confirmado
        </button>
      ) : null}

      <form className="simulator-chat-form" onSubmit={sendMessage}>
        <input
          aria-label="Mensaje para el chat de Discord"
          disabled={!showAuthenticatedChat || !state.joined || state.busy || Boolean(state.storageError) || isIntentBlockingForUi(state.intent)}
          maxLength={500}
          onChange={(event) => setMessage(event.target.value)}
          placeholder={showAuthenticatedChat ? "Escribe en Discord..." : "Chat solo para pilotos"}
          value={message}
        />
        <button disabled={!canSend} type="submit" aria-label={state.busy ? "Enviando mensaje" : "Enviar mensaje"}>
          <Send aria-hidden="true" size={16} />
        </button>
      </form>
    </article>
  );
}

function ChatMessage({ item, mine }) {
  const timeLabel = safeSimulatorChatTime(item.createdAt);
  return (
    <div className={`simulator-chat-message ${mine ? "is-mine" : ""}`}>
      <strong>{item.author?.displayName || "Piloto Nitro"}</strong>
      <p>{item.message}</p>
      {item.createdAt ? <time dateTime={item.createdAt}>{timeLabel}</time> : <span className="simulator-chat-time">{timeLabel}</span>}
    </div>
  );
}

function initialState() {
  return {
    loading: true,
    authState: "loading",
    socketConnected: false,
    joined: false,
    busy: false,
    error: "",
    notice: "",
    storageError: "",
    user: null,
    intent: null,
    messages: [],
  };
}

function adapterIntentOptions(adapters, guard) {
  const options = { guard };
  if (Object.prototype.hasOwnProperty.call(adapters, "storage")) options.storage = adapters.storage;
  if (Object.prototype.hasOwnProperty.call(adapters, "legacyStorage")) options.legacyStorage = adapters.legacyStorage;
  if (Object.prototype.hasOwnProperty.call(adapters, "cryptoApi")) options.cryptoApi = adapters.cryptoApi;
  if (Object.prototype.hasOwnProperty.call(adapters, "lockManager")) options.lockManager = adapters.lockManager;
  if (typeof adapters.now === "function") options.now = adapters.now;
  return options;
}

function positiveId(value) {
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

function isIntentBlockingForUi(intent) {
  return Boolean(intent && intent.status !== "failed");
}

function restoredIntentNotice(intent) {
  if (!intent) return "";
  if (intent.status === "completed") return "Hay un mensaje confirmado pendiente de cargar desde el historial.";
  if (intent.status === "failed") return "El intento anterior falló. Puedes enviar el borrador nuevamente con una identidad nueva.";
  return "Hay un envío pendiente. Consulta su estado antes de intentar otro mensaje.";
}

function outcomeNotice(outcome) {
  if (!outcome?.found || outcome.status === "unknown") return "No pudimos confirmar el envío. Conservamos la solicitud para consulta manual.";
  if (outcome.status === "completed") return "El mensaje está confirmado. Carga el historial para recuperarlo sin reenviar.";
  if (outcome.status === "failed") return "Nitro confirmó que el intento falló. Puedes enviar nuevamente de forma manual.";
  if (outcome.status === "reconciling") return "Nitro está conciliando el resultado. Consulta la misma solicitud más tarde.";
  return "El mensaje sigue en proceso. Consulta la misma solicitud sin reenviar.";
}

function safePublicError(error, fallback) {
  const message = typeof error?.message === "string" ? error.message.trim() : "";
  return message && message.length <= 240 ? message : fallback;
}
